import BackButton from '../components/BackButton';
import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function AdminContracts() {
  const { setPage, isAdmin } = useApp();
  const { t } = useI18n();
  const [tab, setTab] = useState('gateway');
  const [gatewayContracts, setGatewayContracts] = useState([]);
  const [workContracts, setWorkContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    loadContracts();
  }, [isAdmin]);

  async function loadContracts() {
    setLoading(true);
    try {
      const [gw, wc] = await Promise.all([
        api.get('/gateway/admin/list'),
        api.get('/work-contract/admin/list'),
      ]);
      setGatewayContracts(gw.data || []);
      setWorkContracts(wc.data || []);
    } catch (e) {
      setError(e.userMessage);
    } finally {
      setLoading(false);
    }
  }

  if (!isAdmin) {
    return <div className="page"><BackButton /><h2>⛔ Доступ запрещён</h2></div>;
  }

  const statusColor = (s) => {
    if (s === 'active') return '#00cec9';
    if (s === 'signed') return '#6c5ce7';
    if (s === 'suspended') return '#ff6b6b';
    return '#888';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('ru-RU') : '—';

  return (
    <div className="page"><BackButton />
      <h1>📋 Админ: Договоры</h1>

      {error && <div className="error-box">{error}</div>}

      <div className="admin-tabs" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button 
          className={`btn ${tab === 'gateway' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setTab('gateway')}
        >
          🚀 Gateway ({gatewayContracts.length})
        </button>
        <button 
          className={`btn ${tab === 'work' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setTab('work')}
        >
          📝 Work ({workContracts.length})
        </button>
      </div>

      {loading ? (
        <div className="loader">{t('common.loading')}</div>
      ) : tab === 'gateway' ? (
        <div className="admin-list">
          <h3>🚀 Gateway Contracts (1200/350/6%)</h3>
          {gatewayContracts.length === 0 ? (
            <p style={{ opacity: 0.6 }}>Нет подписанных договоров</p>
          ) : (
            <div className="list">
              {gatewayContracts.map(c => (
                <div key={c.id} className="list-item" style={{ borderLeft: `3px solid ${statusColor(c.status)}` }}>
                  <div className="list-main">
                    <div className="list-title">
                      #{c.id} — {c.user?.first_name} {c.user?.last_name}
                    </div>
                    <div className="list-sub">
                      🏢 {c.company?.name || '—'} • TG: {c.user?.telegram_id}
                    </div>
                    <div className="list-sub">
                      📅 Подписан: {formatDate(c.signed_at)}
                    </div>
                  </div>
                  <div className="list-right">
                    <span className="status-badge" style={{ background: statusColor(c.status) }}>
                      {c.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="admin-stats" style={{ marginTop: 20, padding: 16, background: 'rgba(108,92,231,0.1)', borderRadius: 12 }}>
            <h4>📊 Статистика Gateway</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#00cec9' }}>
                  {gatewayContracts.filter(c => c.status === 'active').length}
                </div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>Активных</div>
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#6c5ce7' }}>
                  {gatewayContracts.filter(c => c.status === 'active').length * 1200} PLN
                </div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>Активации</div>
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ffc107' }}>
                  {gatewayContracts.filter(c => c.status === 'active').length * 350} PLN
                </div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>Абонемент/мес</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="admin-list">
          <h3>📝 Work Contracts (Легализация)</h3>
          {workContracts.length === 0 ? (
            <p style={{ opacity: 0.6 }}>Нет подписанных договоров</p>
          ) : (
            <div className="list">
              {workContracts.map(c => (
                <div key={c.id} className="list-item" style={{ borderLeft: `3px solid ${statusColor(c.status)}` }}>
                  <div className="list-main">
                    <div className="list-title">
                      #{c.id} — {c.user?.first_name} {c.user?.last_name}
                    </div>
                    <div className="list-sub">
                      🏢 {c.company?.name || '—'} • Тип: <strong>{c.contract_type}</strong>
                    </div>
                    <div className="list-sub">
                      👔 {c.role_text || '—'} {c.kup_50_enabled && '• 50% KUP ✅'}
                    </div>
                    <div className="list-sub">
                      📅 Подписан: {formatDate(c.signed_at)}
                      {c.krs_verified && ' • KRS ✅'}
                    </div>
                  </div>
                  <div className="list-right">
                    <span className="status-badge" style={{ background: statusColor(c.status) }}>
                      {c.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="admin-stats" style={{ marginTop: 20, padding: 16, background: 'rgba(0,206,201,0.1)', borderRadius: 12 }}>
            <h4>📊 Статистика Work Contracts</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#6c5ce7' }}>
                  {workContracts.filter(c => c.contract_type === 'umowa_o_dzielo').length}
                </div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>Dzieło</div>
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#00cec9' }}>
                  {workContracts.filter(c => c.contract_type === 'umowa_zlecenie').length}
                </div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>Zlecenie</div>
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ffc107' }}>
                  {workContracts.filter(c => c.contract_type === 'manager').length}
                </div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>Manager</div>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 14 }}>
                50% KUP: <strong>{workContracts.filter(c => c.kup_50_enabled).length}</strong> договоров
              </div>
              <div style={{ fontSize: 14 }}>
                KRS verified: <strong>{workContracts.filter(c => c.krs_verified).length}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
        <button className="btn btn-secondary" onClick={loadContracts}>🔄 Обновить</button>
        <button className="btn btn-secondary" onClick={() => setPage('admin_clients')}>👥 Клиенты</button>
        <button className="btn btn-secondary" onClick={() => setPage('dashboard')}>← Назад</button>
      </div>
    </div>
  );
}
